import { type FormatMoneyOptions, type MoneyField, buildNumberFormat, formatMoney } from '@insify/global-money';

/**   formatToParts returns an array of objects:
 * @example
 * [
 *   { type: 'currency', value: '€' },
 *   { type: 'literal', value: ' ' },
 *   { type: 'integer', value: '0' },
 *   { type: 'decimal', value: ',' },
 *   { type: 'fraction', value: '00' }
 * ]
 * Then we substitute the integer and fraction parts with placeholders.
 */
const formatMoneyToParts = ({ cents, currency }: MoneyField, locale: string, options?: FormatMoneyOptions) => {
  return buildNumberFormat(currency, locale, options).formatToParts(parseInt(cents, 10) / 100);
};

type FormatCentsOptions = {
  thousandSeparator?: boolean;
  decimals?: boolean;
};

const defaultOptions: FormatCentsOptions = {
  thousandSeparator: true,
  decimals: true,
};

/**
 * @deprecated -- use formatMoney instead
 */
function formatCents(cents = '', options?: FormatCentsOptions, locale?: string): string {
  if (Number.isNaN(Number(cents))) {
    throw new Error('formatCents only accepts digits as a string');
  }

  const newOptions = { ...defaultOptions, ...options };
  const { thousandSeparator, decimals } = newOptions;

  let formatMoneyOptions: FormatMoneyOptions = { withoutCurrency: true };

  if (!thousandSeparator) {
    formatMoneyOptions = { ...formatMoneyOptions, withoutGrouping: true };
  }

  if (!decimals) {
    formatMoneyOptions = { ...formatMoneyOptions, withoutFraction: true };
  }

  return formatMoney({ cents, currency: 'EUR' }, locale ?? 'nl', formatMoneyOptions);
}

function formatMoneyPlaceholder(
  currency: MoneyField['currency'],
  locale: string,
  options?: Pick<FormatMoneyOptions, 'withoutFraction' | 'withoutCurrency'>,
): string {
  const parts = formatMoneyToParts({ cents: '0', currency }, locale, options);
  return parts.reduce((acc, part) => {
    if (part.type === 'integer' || part.type === 'fraction') {
      return acc + '--';
    }
    return acc + part.value;
  }, '');
}

function dynamicMoney(money: MoneyField, locale: string, isLoading: boolean, options?: FormatMoneyOptions): string {
  return isLoading ? formatMoneyPlaceholder(money.currency, locale, options) : formatMoney(money, locale, options);
}

export { formatCents, formatMoneyPlaceholder, dynamicMoney, formatMoneyToParts };
